export interface ISolutionCategoryStructure {
  id: number;
  name: string;
  description: string | null;
  folders: ISolutionFolderStructure[];
}

export interface ISolutionFolderStructure {
  id: number;
  name: string;
  description: string | null;
}

export interface ISolutionCategoryAndFolderStructure {
  categories: ISolutionCategoryStructure[];
}

export enum TicketStatus {
  Open = 2,
  Pending = 3,
  Resolved = 4,
  Closed = 5,
}

export interface ISolutionArticle {
  id: string;
  freshdeskId: number;
  languages: string[];
  categoryId: number;
  categoryName: string; // Note: non-translated articles get this in default language too
  folderId: number;
  folderName: string; // Note: non-translated articles get this in default language too
  title: string;
  descriptionText: string;
  descriptionHtml: string;
  status: number;
  tags: string[];
  attachments: IndexAttachment[];
  createdAt: string; // ISOString
  updatedAt: string; // ISOString
  refreshed: string; // ISOString
}

export interface ISolutionArticleSearchResult {
  count: number;
  value: ISolutionArticle[];
}

export interface ISolutionArticleSearchQuery {
  lang: string;
  term?: string;
  categoryId?: number;
  folderId?: number;
  tag?: string;
  sort?: string;
  top?: number;
  skip?: number;
}

export interface ITicketSearchResult {
  count: number;
  value: IndexTicket[] | null;
}

export interface ISupportRequest {
  email?: string;
  subject: string;
  description: string;
  descriptionText: string;
  attachments?: {
    freshdeskId: number;
    name: string;
    size: number;
  }[];
  name: string;
  userId: string;
  id: string;
  toEmails?: string[];
  createdAt: Date;
  updatedAt: Date;
  tags?: string[];
  refreshed: Date;
  avatarUrl?: string;
  conversation: ISupportRequestConversationItem[];
  status: TicketStatus;
}

// Copypaste from microservice types
export interface IndexAttachment {
  freshdeskId: number;
  name: string;
  size: number;
}

export interface IndexConversation {
  freshdeskId: number;
  bodyText: string;
  bodyHtml: string;
  senderName: string;
  senderEmail?: string;
  senderAvatarUrl?: string;
  attachments: IndexAttachment[];
  createdAt: Date;
  updatedAt: Date;
}

export interface IndexTicket {
  id: string;
  freshdeskId: number;
  subject: string;
  descriptionText: string;
  descriptionHtml: string;
  requesterName: string;
  requesterEmail?: string;
  requesterAvatarUrl?: string;
  status: number;
  type?: string;
  tags?: string[];
  attachments: IndexAttachment[];
  createdAt: Date;
  updatedAt: Date;
  entityUpdatedAt: Date; // contains the latest updatedAt value of the ticket and all the conversation messages
  // conversation: IndexConversation[];
  conversation: {
    freshdeskId: number;
    bodyText: string;
    bodyHtml: string;
    senderName: string;
    senderEmail?: string;
    senderAvatarUrl?: string;
    attachments: IndexAttachment[];
    createdAt: Date;
    updatedAt: Date;
  }[];
  partnerId: string;
  refreshed: Date;
}

export interface ISupportRequestInput {
  categories: string[];
  fields: ISupportRequestField[];
  subject: string;
  description: string;
  attachments?: File[];
  ticketRoutingInfo: ITicketRoutingInfo;
}

export interface ISupportRequestUpload {
  email: string;
  partnerId: string;
  subject: string;
  description: string;
  routingInfo: string;
  attachments?: {
    name: string;
    file: string;
  }[];
}

export interface ISupportRequestConversationItem {
  email?: string;
  bodyText: string;
  bodyHtml: string;
  id: number;
  avatarUrl?: string;
  createdAt: Date;
  updatedAt: Date;
  attachments?: {
    freshdeskId: number;
    name: string;
    size: number;
  }[];
  name: string;
}

// New support request conversation reply in backend
export interface ISupportRequestConversationItemUpload {
  email: string;
  message: string;
  attachments?: {
    name: string;
    file: string;
  }[];
  ticketId: string;
  partnerId: string;
  name: string;
}

// Create new support request conversation reply in UI
export interface ISupportRequestConversationItemInput {
  message: string;
  ticketId: string;
  attachments?: File[];
  name: string;
}

export interface ISupportRequestFilters {
  status?: "CLOSED" | "OPEN" | "ANY";
  requesterEmail?: string;
  term?: string;
  partnerId: string;
  id?: string;
  pageState: {
    top: number;
    skip: number;
  };
}

export enum ITicketRoutingInfo {
  empty = "",
  Shipment_Inquiry = "Shipment_Inquiry",
  Order_CorrectShipped = "Order_CorrectShipped",
  Order_ChangeNonShipped = "Order_ChangeNonShipped",
  PartnerPortal = "PartnerPortal",
  License_UpgradeDowngrade = "License_UpgradeDowngrade",
  License_Access = "License_Access",
  License_Other = "License_Other",
  License_OwnershipChange = "License_OwnershipChange",
  Return_MaintenanceReturn = "Return_MaintenanceReturn",
  Return_SalesReturn = "Return_SalesReturn",
  TechnicalSupportWithoutCountryCode = "TechnicalSupport",
  FinancialInquiry = "FinancialInquiry", // TODO waiting for FinancialInquiry variations specs from iLOQ but the first version can be released this way
}

export interface ISupportRequestField {
  label: string;
  value: string;
}
